import React from "react";
import { FolderItemComponent } from "./folderItem";
import { FileItemComponent } from "@/components/feature/layout/left-sidebar/fileItem";
import { type FolderItem, type Item, type Location as FileLocation, FileItem } from "@/types/fse";
import { FlattenedItem } from "@/modules/chat/chat-ui.types";

type ContextFileViewerProps = {
  loading: boolean;
  error: string | null | unknown;
  rootItem?: Item | null;
  flattenedItems: FlattenedItem[];
  loadFileSystem: () => void;
  showFolders: boolean;
  toggleFolder: (folderId: string) => void;
  handleFileClick: (file: FileItem) => void;
  handleFileSelect: (file: FileItem, isSelected: boolean) => void;
  handleAskFile: (fileId: string) => void;
  selectedFiles: Set<string>;
  getParentLocations: (item: FileItem) => FileLocation[];
  handleFolderToggle: (folderId: string) => void;
  getFolderSelectionState: (folderId: string) => "all" | "some" | "none";
};

function isFileItem(item: FlattenedItem): item is FlattenedItem & FileItem {
  return item.type === "file";
}

function isFolderItem(item: FlattenedItem): item is FlattenedItem & FolderItem {
  return item.type === "folder";
}

export const ContextFileViewer: React.FC<ContextFileViewerProps> = ({
  loading,
  error,
  rootItem,
  flattenedItems,
  loadFileSystem,
  showFolders,
  toggleFolder,
  handleFileClick,
  handleFileSelect,
  handleAskFile,
  selectedFiles,
  getParentLocations,
  handleFolderToggle,
  getFolderSelectionState,
}) => {
  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <p className="text-red-500">{String(error)}</p>
        <button onClick={loadFileSystem} className="mt-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600">
          Retry
        </button>
      </div>
    );
  }

  if (!rootItem) {
    return <div className="p-4">No data available</div>;
  }

  return (
    <div className="space-y-2">
      {flattenedItems.map(item => (
        <div key={item.id}>
          {isFolderItem(item) && showFolders && (
            <FolderItemComponent
              item={item}
              toggleFolder={toggleFolder}
              handleFolderToggle={handleFolderToggle}
              getFolderSelectionState={getFolderSelectionState}
            />
          )}
          {isFileItem(item) && (
            <FileItemComponent
              item={item}
              parentLocations={getParentLocations(item)}
              handleFileClick={handleFileClick}
              handleFileSelect={handleFileSelect}
              handleAskFile={handleAskFile}
              selectedFiles={selectedFiles}
            />
          )}
        </div>
      ))}
    </div>
  );
};
